import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from '@/hooks/useToast'
import ToastContainer from '@/components/ui/ToastContainer'
import { API_BASE_URL } from '@/services/api'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

interface ContaReceber {
  id: number
  descricao: string
  valor: number
  data_vencimento: string
  data_pagamento?: string
  status: string
  categoria?: { id: number; nome: string }
  cliente?: { id: number; nome: string }
}

interface DashboardStats {
  total_pendente: number
  total_vencido: number
  total_recebido_mes: number
  valor_pendente: number
  valor_vencido: number
  valor_recebido_mes: number
  ticket_medio: number
  total_contas: number
}

const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899']

const ContasReceberDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const { toasts, success, error, removeToast } = useToast()

  const [receivables, setReceivables] = useState<ContaReceber[]>([])
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)

      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Erro ao carregar contas a receber')
      }

      const data: ContaReceber[] = await response.json()
      setReceivables(data)

      // Calcular estatísticas
      const now = new Date()
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)

      const pendentes = data.filter(c => c.status === 'PENDENTE' || c.status === 'pending')
      const vencidas = data.filter(c => {
        const dueDate = new Date(c.data_vencimento)
        return (c.status === 'PENDENTE' || c.status === 'pending') && dueDate < now
      })
      const recebidasMes = data.filter(c => {
        if (!c.data_pagamento) return false
        const paymentDate = new Date(c.data_pagamento)
        return (c.status === 'PAGO' || c.status === 'paid') && paymentDate >= startOfMonth
      })

      const calculatedStats: DashboardStats = {
        total_pendente: pendentes.length,
        total_vencido: vencidas.length,
        total_recebido_mes: recebidasMes.length,
        valor_pendente: pendentes.reduce((sum, c) => sum + parseFloat(c.valor.toString()), 0),
        valor_vencido: vencidas.reduce((sum, c) => sum + parseFloat(c.valor.toString()), 0),
        valor_recebido_mes: recebidasMes.reduce((sum, c) => sum + parseFloat(c.valor.toString()), 0),
        ticket_medio: data.length > 0 ? data.reduce((sum, c) => sum + parseFloat(c.valor.toString()), 0) / data.length : 0,
        total_contas: data.length
      }

      setStats(calculatedStats)
    } catch (err: any) {
      error('Erro ao carregar dashboard', err.message)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Preparar dados para gráfico de pizza (status)
  const statusChartData = stats ? [
    { name: 'Pendentes', value: stats.valor_pendente },
    { name: 'Vencidas', value: stats.valor_vencido },
    { name: 'Recebidas este Mês', value: stats.valor_recebido_mes }
  ].filter(item => item.value > 0) : []

  // Preparar dados para gráfico de barras (top 5 clientes)
  const topClientes = receivables
    .reduce((acc: any[], curr) => {
      if (!curr.cliente) return acc
      const existing = acc.find(item => item.cliente_id === curr.cliente!.id)
      if (existing) {
        existing.valor += parseFloat(curr.valor.toString())
        existing.quantidade += 1
      } else {
        acc.push({
          cliente_id: curr.cliente.id,
          cliente_nome: curr.cliente.nome,
          valor: parseFloat(curr.valor.toString()),
          quantidade: 1
        })
      }
      return acc
    }, [])
    .sort((a, b) => b.valor - a.valor)
    .slice(0, 5)

  // Preparar dados para evolução mensal
  const monthlyData = receivables.reduce((acc: any[], curr) => {
    const date = new Date(curr.data_vencimento)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

    const existing = acc.find(item => item.month === monthKey)
    if (existing) {
      existing.total += parseFloat(curr.valor.toString())
      existing.quantidade += 1
    } else {
      acc.push({
        month: monthKey,
        monthLabel: date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        total: parseFloat(curr.valor.toString()),
        quantidade: 1
      })
    }
    return acc
  }, []).sort((a, b) => a.month.localeCompare(b.month)).slice(-6)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ChartBarIcon className="w-7 h-7 text-green-600" />
              Dashboard - Contas a Receber
            </h1>
            <p className="text-gray-600 mt-1">Análise completa de recebimentos e inadimplência</p>
          </div>
          <button
            onClick={() => navigate('/financeiro/contas-receber')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Ver Listagem
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Pendentes</p>
                <p className="text-2xl font-bold">{stats.total_pendente}</p>
                <p className="text-sm mt-1">{formatCurrency(stats.valor_pendente)}</p>
              </div>
              <ClockIcon className="w-8 h-8 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Vencidas</p>
                <p className="text-2xl font-bold">{stats.total_vencido}</p>
                <p className="text-sm mt-1">{formatCurrency(stats.valor_vencido)}</p>
              </div>
              <ExclamationTriangleIcon className="w-8 h-8 text-red-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Recebido este Mês</p>
                <p className="text-2xl font-bold">{stats.total_recebido_mes}</p>
                <p className="text-sm mt-1">{formatCurrency(stats.valor_recebido_mes)}</p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-blue-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Ticket Médio</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.ticket_medio)}</p>
                <p className="text-sm mt-1">{stats.total_contas} contas</p>
              </div>
              <CurrencyDollarIcon className="w-8 h-8 text-purple-200" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Distribuição por Status */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Status</h3>
          {statusChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-12">Nenhum dado disponível</p>
          )}
        </div>

        {/* Bar Chart - Top 5 Clientes */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Clientes</h3>
          {topClientes.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topClientes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="cliente_nome" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="valor" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-12">Nenhum cliente com contas registradas</p>
          )}
        </div>
      </div>

      {/* Line Chart - Evolução Mensal */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Evolução Mensal</h3>
        {monthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#10B981" strokeWidth={2} name="Valor Total" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-gray-500 py-12">Nenhum dado disponível</p>
        )}
      </div>

      {/* Top 10 Contas Pendentes */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top 10 Maiores Contas Pendentes
        </h3>
        <div className="space-y-3">
          {receivables
            .filter(r => r.status === 'PENDENTE' || r.status === 'pending')
            .sort((a, b) => parseFloat(b.valor.toString()) - parseFloat(a.valor.toString()))
            .slice(0, 10)
            .map((receivable, index) => (
              <div
                key={receivable.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => navigate(`/financeiro/contas-receber/${receivable.id}/detalhes`)}
              >
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center font-semibold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{receivable.descricao}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      {receivable.cliente && <span>Cliente: {receivable.cliente.nome}</span>}
                      <span>Venc: {new Date(receivable.data_vencimento).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">{formatCurrency(parseFloat(receivable.valor.toString()))}</p>
                  {receivable.categoria && (
                    <span className="text-xs text-gray-500">{receivable.categoria.nome}</span>
                  )}
                </div>
              </div>
            ))}
          {receivables.filter(r => r.status === 'PENDENTE' || r.status === 'pending').length === 0 && (
            <p className="text-center text-gray-500 py-12">Nenhuma conta pendente</p>
          )}
        </div>
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}

export default ContasReceberDashboard
